import React from 'react';
import { Box, Container, Typography, Grid, Card } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const EditionsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(6, 0),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.05) 0%, rgba(6, 174, 213, 0.05) 100%)',
    zIndex: 0
  }
}));

const EditionCard = styled(Card)(({ theme, delay }) => ({
  height: '100%',
  padding: theme.spacing(3),
  background: 'rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(15px)',
  borderRadius: '24px',
  border: '2px solid rgba(255, 255, 255, 0.15)',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeInUp} 0.8s ease-out ${delay}s both`,
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
    opacity: 0,
    transition: 'opacity 0.4s ease',
  },
  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: '0 20px 60px rgba(255, 107, 53, 0.4)',
    border: '2px solid rgba(255, 107, 53, 0.5)',
    '&::before': {
      opacity: 1,
    }
  }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  marginBottom: theme.spacing(4),
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
  border: '3px solid rgba(255, 107, 53, 0.3)',
  animation: `${floatAnimation} 3s ease-in-out infinite`,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(6, 174, 213, 0.1) 100%)',
    pointerEvents: 'none',
  },
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 25px 80px rgba(255, 107, 53, 0.5)',
    border: '3px solid rgba(255, 107, 53, 0.6)',
  },
  transition: 'all 0.4s ease',
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
  width: '70px',
  height: '70px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${color}20 0%, ${color}40 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  boxShadow: `0 8px 24px ${color}30`,
  transition: 'all 0.3s ease',
  '& svg': {
    fontSize: '2.5rem',
    color: color,
    filter: `drop-shadow(0 0 8px ${color}50)`,
  },
  '&:hover': {
    transform: 'rotate(360deg) scale(1.1)',
    boxShadow: `0 12px 32px ${color}50`,
  }
}));

const CardImageWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '180px',
  borderRadius: '16px',
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  position: 'relative',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.4s ease',
  },
  '&:hover img': {
    transform: 'scale(1.1)',
  }
}));

const EditionsInfo = () => {
  const handleCardClick = () => {
    const pricingSection = document.getElementById('pricing-plans-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const editions = [
    {
      title: 'Beginner',
      ageRange: '3-5 years',
      icon: ChildCareIcon,
      color: '#FF6B35',
      description: 'Simple news stories with pictures',
      image: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/beginner_edition.jpeg',
      delay: 0.2
    },
    {
      title: 'Junior',
      ageRange: '6-9 years',
      icon: SchoolIcon,
      color: '#06AED5',
      description: 'Easy-to-read news for young readers',
      image: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/jun_edition.jpeg',
      delay: 0.4
    },
    {
      title: 'Senior',
      ageRange: '10-15 years',
      icon: EmojiEventsIcon,
      color: '#4CAF50',
      description: 'Focussed on Elder kids for News, Education and Technology',
      image: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/senior_edition.jpeg',
      delay: 0.6
    }
  ];

  return (
    <EditionsContainer>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Main Title */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 50%, #06AED5 70%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              textShadow: '0 0 40px rgba(255, 107, 53, 0.3)',
              animation: `${fadeInUp} 0.8s ease-out`,
            }}
          >
            Our Editions
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: { xs: '1rem', sm: '1.2rem' },
              maxWidth: '600px',
              margin: '0 auto',
              animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
            }}
          >
            Tailored content for every age group
          </Typography>
        </Box>

        {/* Edition Cards */}
        <Grid container spacing={4}>
          {editions.map((edition, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <EditionCard delay={edition.delay} onClick={handleCardClick}>
                {/* Edition Image */}
                <CardImageWrapper>
                  <img
                    src={edition.image}
                    alt={`${edition.title} Edition`}
                  />
                </CardImageWrapper>

                {/* Icon */}
                <IconWrapper color={edition.color} sx={{ width: '60px', height: '60px', mb: 2 }}>
                  <edition.icon sx={{ fontSize: '2rem' }} />
                </IconWrapper>
                
                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    textAlign: 'center',
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    mb: 1,
                  }}
                >
                  {edition.title}
                </Typography>
                
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    background: `linear-gradient(45deg, ${edition.color} 30%, ${edition.color}CC 70%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textAlign: 'center',
                    mb: 2,
                    fontSize: { xs: '1.1rem', sm: '1.3rem' },
                  }}
                >
                  {edition.ageRange}
                </Typography>
                
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    textAlign: 'center',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.6,
                    mb: 2,
                  }}
                >
                  {edition.description}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: edition.color,
                    textAlign: 'center',
                    fontSize: { xs: '1.1rem', sm: '1.3rem' },
                    fontWeight: 700,
                    mt: 'auto',
                    pt: 2,
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    textShadow: `0 0 10px ${edition.color}50`,
                  }}
                >
                  Select →
                </Typography>
              </EditionCard>
            </Grid>
          ))}
        </Grid>

        {/* Bottom Tagline */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: { xs: '0.9rem', sm: '1.1rem' },
              fontStyle: 'italic',
              animation: `${fadeInUp} 0.8s ease-out 0.8s both`,
            }}
          >
            
          </Typography>
        </Box>
      </Container>
    </EditionsContainer>
  );
};

export default EditionsInfo;
